import { Link } from "@inertiajs/react";
import Screenshot from "@/components/help/Screenshot";
import TakeHomeSheetPreview from "@/components/schools/TakeHomeSheetPreview";
import { SCHOOL, CAMPAIGN_ACTIVE, SCHOOL_MILESTONES } from "@/help/fixtures";

export default function PrintingTakeHomeSheets() {
  return (
    <>
      <p>
        Take-home sheets are the last step in the setup guide before launch. A <strong>take-home sheet</strong> is a one-pager that goes home with each student — their name, classroom, activation code, and a QR code that takes their parent straight to the sign-up page. We turn them into a PDF you can print right away, no template editing or setup.
      </p>

      <Screenshot caption="One sheet, ready to print and send home" padded={false}>
        <div className="p-6 bg-secondary/30">
          <TakeHomeSheetPreview
            school={SCHOOL}
            campaign={CAMPAIGN_ACTIVE}
            student={{
              full_name: "Sarah T.",
              first_name: "Sarah",
              classroom_name: "Room 12",
              grade: "Grade 5",
              claim_code: "BEAR-MOON-4217",
            }}
            milestones={SCHOOL_MILESTONES.slice(0, 3)}
          />
        </div>
      </Screenshot>

      <h2>Two ways to make them</h2>
      <p>
        From your dashboard, click <strong>Take-home sheets</strong> for one PDF with the entire school — every student, grouped by classroom, ready to print and split into stacks for each teacher.
      </p>
      <p>
        From a classroom page, click <strong>Sheets</strong> for just that classroom — usually 20–30 sheets. Teachers can only print for their own classrooms; admins can print for any.
      </p>

      <h2>What's on each sheet</h2>
      <p>
        The student's name, classroom, and grade; a big QR code that takes parents straight to the sign-up page; the activation code in case the parent's camera can't read the QR; a quick "1. Activate. 2. Log reading. 3. Share their page." rundown; and the prizes, so families can see what's at stake: your school-wide rewards, their own class's rewards, and what each reader can earn.
      </p>

      <h2>Fitting it on one page</h2>
      <p>
        Every student gets exactly one page. If yours runs long, say with a long list of rewards, we print it slightly smaller so it still fits. To keep it full size, a school admin can open the setup guide's take-home sheets step (before launch), click <strong>Customize &amp; download</strong>, then choose narrower margins or leave the school rewards or student prizes off. You'll see a real sheet for a made-up student, so you can check the layout before printing the whole school. Changing the layout never changes anyone's activation code, so sheets already sent home keep working.
      </p>

      <h2>Printing tips</h2>
      <p>
        Plain paper is fine — QR codes scan cleanly. Printing in black and white? A school admin can switch the sheets to <strong>Black &amp; white</strong> in the same place as the margins, so the colours don't come out as faint greys. It applies to every download, including teachers' class sheets. The back of each sheet is intentionally blank if you want kids to doodle.
      </p>

      <h2>When to print</h2>
      <p>
        Print whenever you'd like before the campaign ends, but aim to have the sheets go home about two weeks before reading starts (or as donations open, if you set a date for that). Families can activate as soon as your campaign is paid for and approved, so send the sheets home after that; then they can share their page with relatives before reading begins. After that, reprint just the classrooms where new students have joined or someone's lost their sheet. You don't have to reprint the whole school each time.
      </p>

      <h2>What's next</h2>
      <ul>
        <li><Link href="/help/articles/setting-school-milestones">Set up rewards</Link> — do these first, so the sheets list them.</li>
        <li><Link href="/help/articles/rotating-activation-codes">Replacing a lost take-home sheet</Link>.</li>
        <li><Link href="/help/articles/activating-your-child">What parents see when they scan the QR</Link>.</li>
      </ul>
    </>
  );
}
